import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import AxiosService from "../../services/AxiosHelper";

const baseURL = process.env.REACT_APP_BASEURL;

export const getAppointments = createAsyncThunk(
    "appointment/getFiltered",
    async ({ businessId, range = 'all', page = 1, limit = 10 }, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "get",
                url: `${baseURL}/appointment/business/${businessId}?range=${range}&page=${page}&limit=${limit}`,
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to fetch appointments");
        }
    }
);

export const addAppointment = createAsyncThunk(
    "appointment/addAppointment",
    async (values, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "post",
                url: baseURL + `/appointment`,
                data: values,
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to add appointment");
        }
    }
);

export const updateAppointment = createAsyncThunk(
    "appointment/updateAppointment",
    async (values, { rejectWithValue }) => {
        try {
            const response = await AxiosService.AxiostRequest({
                method: "put",
                url: baseURL + `/appointment/${values.id}`,
                data: values,
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Update failed");
        }
    }
);

export const deleteAppointment = createAsyncThunk(
    "appointment/deleteAppointment",
    async (id, { rejectWithValue }) => {
        try {
            await AxiosService.AxiostRequest({
                method: "delete",
                url: baseURL + `/appointment/${id}`,
            });
            return id;
        } catch (err) {
            return rejectWithValue(err.response?.data || "Failed to delete appointment");
        }
    }
);

const appointmentSlice = createSlice({
    name: "appointment",
    initialState: {
        appointmentList: [],
        appointments: [],
        pagination: { total: 0, page: 1, limit: 10, pages: 1 },
        loading: false,
    },
    reducers: {
        clearAppointmentState: (state) => {
            state.appointments = [];
            state.appointmentList = [];
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getAppointments.fulfilled, (state, { payload }) => {
            state.appointments = payload.appointments || [];
            state.appointmentList = payload.appointments || []; // Keeping for backward compatibility if needed
            state.pagination = payload.pagination;
            state.loading = false;
        });
        builder.addCase(addAppointment.fulfilled, (state, action) => {
            state.appointments.unshift(action.payload);
            state.appointmentList.unshift(action.payload);
            state.loading = false;
            toast.success("Appointment created successfully");
        });
        builder.addCase(updateAppointment.fulfilled, (state, { payload }) => {
            state.appointments = state.appointments.map((a) =>
                a._id === payload._id ? payload : a
            );
            state.appointmentList = state.appointmentList.map((a) =>
                a._id === payload._id ? payload : a
            );
            state.loading = false;
            toast.success("Appointment updated successfully");
        });
        builder.addCase(deleteAppointment.fulfilled, (state, action) => {
            state.appointments = state.appointments.filter(
                (a) => a._id !== action.payload
            );
            state.appointmentList = state.appointmentList.filter(
                (a) => a._id !== action.payload
            );
            state.loading = false;
            toast.success("Appointment deleted successfully");
        });

        // General Matchers
        builder.addMatcher(
            (action) => action.type.startsWith("appointment/") && action.type.endsWith("/pending"),
            (state) => { state.loading = true; }
        );
        builder.addMatcher(
            (action) => action.type.startsWith("appointment/") && (action.type.endsWith("/fulfilled") || action.type.endsWith("/rejected")),
            (state, action) => {
                state.loading = false;
                if (action.type.endsWith("/rejected")) {
                    toast.error(action.payload?.message || "Something went wrong");
                }
            }
        );
    },
});

export const { clearAppointmentState } = appointmentSlice.actions;
export default appointmentSlice.reducer;
